/* global gdAlertify */
import { useState } from "react";
import axiosInstance from "../../config/axiosConfig";
import { formatMoney, formatDate } from "../../utils/utils";
import BuscarPaciente from "./../components/BuscarPaciente";
import MonthInput from "./MonthInput";

const Cartola = () => {
    const [periodo, setPeriodo] = useState(new Date().toISOString().slice(0, 7)); // Mes actual
    const [filteredMovements, setFilteredMovements] = useState([]);
    const [movements, setMovements] = useState([]);
    const [totalDebe, setTotalDebe] = useState(0);
    const [totalAbonos, setTotalAbonos] = useState(0);
    const [totalSaldo, setTotalSaldo] = useState(0);
    const [file, setFile] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedMovement, setSelectedMovement] = useState(null);
    const [observacion, setObservacion] = useState("");
    const [selectedOption, setSelectedOption] = useState("");
    const [pacienteInfo, setPacienteInfo] = useState(null);

    // Filtrar movimientos por periodo (mes)
    const filtrarMovimientos = async () => {
        try {
            // Llamada a la API para obtener los movimientos por periodo
            const response = await axiosInstance.post("/financiero/cartola/lista", { periodo });

            const movimientosFiltrados = response.data.data;

            setMovements(movimientosFiltrados);
            setFilteredMovements(movimientosFiltrados);

            // Calcular totales
            // Calcular totales con la lógica que depende del tipo_mov
            const totalDebe = movimientosFiltrados.reduce((acc, movement) => {
                if (movement.tipo_mov === "2") {
                    return acc + parseInt(movement.monto); // Sumar a "Debe" si tipo_mov es 2 (cargo)
                }
                return acc;
            }, 0);

            const totalAbonos = movimientosFiltrados.reduce((acc, movement) => {
                if (movement.tipo_mov === "1") {
                    return acc + parseInt(movement.monto); // Sumar a "Abono" si tipo_mov es 1 (abono)
                }
                return acc;
            }, 0);

            // El saldo es el último valor de balance, o el saldo del último movimiento
            const totalSaldo = movimientosFiltrados.length > 0
                ? parseInt(movimientosFiltrados[movimientosFiltrados.length - 1].saldo)
                : 0;

            setTotalDebe(totalDebe);
            setTotalAbonos(totalAbonos);
            setTotalSaldo(totalSaldo);
        } catch (error) {
            console.error("Error al obtener los movimientos:", error);
        }
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleSeleccionPaciente = (paciente) => {
        setPacienteInfo(paciente);
    };

    const handleUploadFile = async () => {
        if (file) {
            const formData = new FormData();
            formData.append("file", file);

            try {
                await axiosInstance.post("/financiero/cartola/subir", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
                alert("Archivo subido con éxito");
                setShowModal(false);
            } catch (error) {
                console.error("Error al subir el archivo:", error);
                alert("Hubo un error al subir el archivo");
            }
        } else {
            alert("Por favor, seleccione un archivo para subir.");
        }
    };

    const handleAssignClick = (movement) => {
        setSelectedMovement(movement);
        setObservacion('');
        setPacienteInfo(null);
        setShowAssignModal(true);
    };

    const handleAssignSubmit = async () => {
        if (!pacienteInfo) {
            alert("Debe seleccionar un paciente antes de asignar.");
            return;
        }
    
        try {
            const response = await axiosInstance.post("/financiero/cartola/asignar", {
                id_movimiento: selectedMovement.id_movimiento,
                paciente_id: pacienteInfo.id,
                observacion: observacion
            });
    
            if (response.data.error === false) {
                gdAlertify.alertSuccess("Movimiento asignado con éxito.");
                setShowAssignModal(false);
                filtrarMovimientos();
            } else {
                gdAlertify.notiError("Hubo un problema al asignar el movimiento.");
            }
        } catch (error) {
            gdAlertify.notiError("Error al asignar el movimiento.");
        }
    };

    return (
        <div className="container mt-4">
            <div className="card p-4 shadow">
                {/* Encabezado */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3>Cartola Bancaria</h3>
                    <div className="d-flex align-items-center">
                        <label className="form-label me-2">Período:</label>
                        <MonthInput
                            className="form-control me-3"
                            value={periodo}
                            onChange={(e) => setPeriodo(e.target.value)}
                        />
                        <button className="btn btn-primary" onClick={filtrarMovimientos}>
                            Filtrar
                        </button>
                        <button className="btn btn-secondary ms-3" onClick={() => setShowModal(true)}>
                            Subir archivo
                        </button>
                    </div>
                </div>

                {/* Resumen Global */}
                <div className="row mb-3">
                    <div className="col-md-4">
                        <div className="alert alert-info">
                            <strong>Total Debe:</strong> ${formatMoney(totalDebe)}
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="alert alert-success">
                            <strong>Total Abonos:</strong> ${formatMoney(totalAbonos)}
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="alert alert-warning">
                            <strong>Diferencia (Saldo):</strong> ${formatMoney(totalSaldo)}
                        </div>
                    </div>
                </div>

                {/* Tabla de Movimientos */}
                <table className="table table-striped table-bordered">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Concepto</th>
                            <th>Debe</th>
                            <th>Abono</th>
                            <th>Saldo</th>
                            <th>Observación</th>
                            <th>Acción</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMovements.length > 0 ? (
                            filteredMovements.map((movement) => (
                                <tr key={movement.id_movimiento}>
                                    <td>{formatDate(movement.fecha)}</td>
                                    <td>{movement.detalle}</td>

                                    {/* Columna "Debe" */}
                                    <td className={movement.tipo_mov === "1" ? "text-danger" : ""}>
                                        {movement.tipo_mov === "1" ? `$${formatMoney(movement.monto)}` : "0"}
                                    </td>

                                    {/* Columna "Abono" */}
                                    <td className={movement.tipo_mov === "2" ? "text-success" : ""}>
                                        {movement.tipo_mov === "2" ? `$${formatMoney(movement.monto)}` : "0"}
                                    </td>

                                    <td>${formatMoney(movement.saldo)}</td>
                                    <td>{movement.observacion || ""}</td>
                                    <td>
                                    {movement.tipo_mov === "2" && !movement.id_referencia ? (
                                            <button className="btn btn-success ms-3" onClick={() => handleAssignClick(movement)}>
                                                Asignar
                                            </button>
                                        ) : ('')}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={7} className="text-center text-muted">
                                    No hay movimientos para este período.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div
                    className="modal fade show"
                    style={{ display: "block" }}
                    tabIndex="-1"
                    aria-labelledby="exampleModalLabel"
                    aria-hidden="true"
                >
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="exampleModalLabel">
                                    Subir Archivo
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    aria-label="Close"
                                    onClick={() => setShowModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <input
                                    type="file"
                                    className="form-control"
                                    onChange={handleFileChange}
                                />
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    data-bs-dismiss="modal"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cerrar
                                </button>
                                <button type="button" className="btn btn-primary" onClick={handleUploadFile}>
                                    Subir
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAssignModal && (
                <div
                    className="modal fade show"
                    style={{ display: "block" }}
                    tabIndex="-1"
                    aria-labelledby="assignModalLabel"
                    aria-hidden="true"
                >
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="assignModalLabel">
                                    Asignar Movimiento
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    aria-label="Close"
                                    onClick={() => setShowAssignModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <BuscarPaciente
                                        onSelect={handleSeleccionPaciente}
                                        valoresTurnos={true}
                                        conAcuerdos={true}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Observación</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={observacion}
                                        onChange={(e) => setObservacion(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    data-bs-dismiss="modal"
                                    onClick={() => setShowAssignModal(false)}
                                >
                                    Cerrar
                                </button>
                                <button type="button" className="btn btn-primary" onClick={handleAssignSubmit}>
                                    Asignar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Cartola;